import {
  UserLoginResDto,
  UserLoginDto,
  UserRefreshTokenDto,
  UserSetPasswordDto,
  UserRefreshTokenResDto,
  UserVerifyCodeDto,
} from '@docbay/schemas';
import axiosInstance from './axiosInstance';

import { ForgotPasswordDto } from 'common/auth/signIn/models';
import { getLanguageFromLocalStorage } from 'application/helpers/language';
import axiosNonTokenInstance from './axiosNonTokenInstance';
import { LoginResponseHrd } from 'common/auth/signIn/Login';

/**
 * UsersAPI provides methods for interacting with the users API.
 */
export const UsersAPI = {
  /**
   * Logs in a user.
   * @param data - The login data, including the username and password.
   * @returns A promise that resolves to the login response DTO.
   */
  usersLogin: (data: UserLoginDto) =>
    axiosNonTokenInstance.post<LoginResponseHrd>('/users/login', data),

  /**
   * Refreshes a user's access token using their refresh token.
   * @param data - The refresh token data, including the refresh token.
   * @returns A promise that resolves to the refresh token response DTO.
   */
  usersRefreshToken: (data: UserRefreshTokenDto) =>
    axiosNonTokenInstance.post<UserRefreshTokenResDto>(
      '/users/refresh-token',
      data,
    ),

  /**
   * Initiates a password reset for a user.
   * @param data - The forgot password data, including the email and captcha token.
   * @returns A promise that resolves to the response from the server.
   */
  usersForgotPassword: (data: ForgotPasswordDto) => {
    const { captchaToken, ...restData } = data;
    return axiosNonTokenInstance.post('/users/forgot-password', restData, {
      headers: {
        recaptcha: captchaToken,
      },
    });
  },

  /**
   * Verifies a code for a user.
   * @param data - The verify code data, including the code and other relevant information.
   * @returns A promise that resolves to the response from the server.
   */
  usersVerifyCode: (data: UserVerifyCodeDto) => {
    const language = getLanguageFromLocalStorage();
    return axiosNonTokenInstance.post('/users/verify-code', {
      ...data,
      language,
    });
  },

  /**
   * Sets a new password for a user.
   * @param data - The set password data, including the new password and other relevant information.
   * @returns A promise that resolves to the response from the server.
   */
  usersCreateNewPassword: (data: UserSetPasswordDto) =>
    axiosNonTokenInstance.post('/users/password', data),

  /**
   * Signs out a user.
   * @returns A promise that resolves to the response from the server.
   */
  signOut: () => axiosInstance.post('/users/logout'),

  /**
   * Sets the language for a user's profile.
   * @param languageIso - The ISO code for the language to set.
   * @returns A promise that resolves to the response from the server.
   */
  setUserLanguage: (languageIso: string) =>
    axiosInstance.post('/users/change-language', { languageIso }),

  /**
   * Fetches a user's profile information.
   * @returns A promise that resolves to the user's profile data.
   */
  fetchUserProfile: () => axiosInstance.get('/hr-department-user/profile'),
};
